import { useFormik } from 'formik'
import { FC } from 'react'
import {
  Button,
  FormInput,
  FormSelect,
  FormTextArea,
  FormToggle,
  IOption,
  LoadingIndicator,
  useModal,
} from 'app/components'
import {
  ButtonType,
  CargoType,
  CategoriaNotificacaoType,
  CriterioAtendimentoType,
  OptionsHelper,
  SegmentoNotificacaoType,
  showAlert,
  showError,
} from '_metronic/helpers'
import { sendNotificacao } from '../../core/_requests'
import { NotificacaoDtoEnvio } from '../../core/_models'
import { notificacaoEnviarSchema } from './validation/notificacaoEnviarSchema'
import { useT } from '_metronic/i18n/Metronici18n'

const initialValues = {
  titulo: '',
  texto: '',
  categoria: null as IOption<CategoriaNotificacaoType> | null,
  segmento: null as IOption<SegmentoNotificacaoType> | null,
  pessoas: [] as IOption<number>[],
  empresas: [] as IOption<number>[],
  unidades: [] as IOption<number>[],
  especialidades: [] as IOption<number>[],
  cargo: null as IOption<CargoType> | null,
  apenasVinculoLauduz: false,
  criterioAtendimento: null as IOption<CriterioAtendimentoType> | null,
  enviarPush: true,
}

export const NotificacaoEnviarModal: FC = () => {
  const { closeModal } = useModal()
  const { t } = useT()

  const formik = useFormik({
    initialValues,
    validationSchema: notificacaoEnviarSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)

      const segmento = values.segmento?.value as SegmentoNotificacaoType

      try {
        const data: NotificacaoDtoEnvio = {
          titulo: values.titulo,
          texto: values.texto,
          categoria: values.categoria?.value as CategoriaNotificacaoType,
          segmento,
          pessoas:
            segmento === SegmentoNotificacaoType.Lista
              ? values.pessoas.map((pessoa) => pessoa.value)
              : undefined,
          empresas:
            segmento === SegmentoNotificacaoType.Atributo && values.empresas.length > 0
              ? values.empresas.map((empresa) => empresa.value)
              : undefined,
          unidades:
            segmento === SegmentoNotificacaoType.Atributo && values.unidades.length > 0
              ? values.unidades.map((unidade) => unidade.value)
              : undefined,
          especialidades:
            segmento === SegmentoNotificacaoType.Atributo && values.especialidades.length > 0
              ? values.especialidades.map((especialidade) => especialidade.value)
              : undefined,
          cargo: segmento === SegmentoNotificacaoType.Atributo ? values.cargo?.value : undefined,
          apenasVinculoLauduz:
            segmento === SegmentoNotificacaoType.Atributo ? values.apenasVinculoLauduz : undefined,
          criterioAtendimento:
            segmento === SegmentoNotificacaoType.Atendimento_Aberto
              ? values.criterioAtendimento?.value
              : undefined,
          enviarPush: values.enviarPush,
        }

        await sendNotificacao(data)

        await showAlert({
          title: t('common.success'),
          text: 'Notificação enviada com sucesso!',
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const resetCamposSegmento = () => {
    formik.setFieldValue('pessoas', [])
    formik.setFieldValue('empresas', [])
    formik.setFieldValue('unidades', [])
    formik.setFieldValue('especialidades', [])
    formik.setFieldValue('cargo', null)
    formik.setFieldValue('apenasVinculoLauduz', false)
    formik.setFieldValue('criterioAtendimento', null)
  }

  const segmento = formik.values.segmento?.value as SegmentoNotificacaoType | undefined

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormInput
        formik={formik}
        name='titulo'
        entity='titulo'
        className='mb-3'
        infoText='Título da notificação'
        isRequired
      />
      <FormTextArea
        formik={formik}
        name='texto'
        entity='mensagem'
        className='mb-3'
        infoText='Mensagem da notificação'
        isRequired
      />

      <div className='row'>
        <FormSelect
          formik={formik}
          name='categoria'
          entity='categoria'
          fetchOptions={OptionsHelper.getOptionsCategoriasNotificacao}
          isDisabled={formik.isSubmitting}
          className='col-md-6 mb-3'
          isRequired
        />
        <FormSelect
          formik={formik}
          name='segmento'
          label='Enviar para'
          fetchOptions={OptionsHelper.getOptionsSegmentosNotificacao}
          onChange={resetCamposSegmento}
          className='col-md-6 mb-3'
          isDisabled={formik.isSubmitting}
          isRequired
        />
      </div>

      {segmento === SegmentoNotificacaoType.Lista && (
        <FormSelect
          formik={formik}
          name='pessoas'
          entity='pessoa'
          fetchOptions={OptionsHelper.getOptionsPessoas}
          isDisabled={formik.isSubmitting}
          className='mb-3'
          isMulti
          isRequired
        />
      )}

      {segmento === SegmentoNotificacaoType.Atributo && (
        <>
          <div className='row'>
            <FormSelect
              formik={formik}
              name='empresas'
              entity='empresa'
              fetchOptions={OptionsHelper.getOptionsEmpresas}
              isDisabled={formik.isSubmitting}
              className='col-md-6 mb-3'
              isMulti
            />
            <FormSelect
              formik={formik}
              name='unidades'
              entity='unidade'
              fetchOptions={OptionsHelper.getOptionsUnidadesTodas}
              isDisabled={formik.isSubmitting}
              className='col-md-6 mb-3'
              isMulti
            />
          </div>
          <div className='row'>
            <FormSelect
              formik={formik}
              name='especialidades'
              entity='especialidade'
              fetchOptions={OptionsHelper.getOptionsEspecialidades}
              isDisabled={formik.isSubmitting}
              className='col-md-6 mb-3'
              isMulti
            />
            <FormSelect
              formik={formik}
              name='cargo'
              entity='cargo'
              fetchOptions={OptionsHelper.getOptionsCargos}
              isDisabled={formik.isSubmitting}
              className='col-md-6 mb-3'
            />
          </div>
          <FormToggle
            formik={formik}
            name='apenasVinculoLauduz'
            label='Somente vínculos Lauduz'
            isDisabled={formik.isSubmitting}
            className='mb-3'
          />
        </>
      )}

      {segmento === SegmentoNotificacaoType.Atendimento_Aberto && (
        <FormSelect
          formik={formik}
          name='criterioAtendimento'
          entity='criterio_atendimento'
          fetchOptions={OptionsHelper.getOptionsCriterioAtendimentoNotificacao}
          isDisabled={formik.isSubmitting}
          className='mb-3'
          isRequired
        />
      )}

      <FormToggle
        formik={formik}
        name='enviarPush'
        label='Enviar também como notificação push'
        infoText='Desligado, a notificação aparece só no sino e na lista de notificações, sem alerta no navegador'
        isDisabled={formik.isSubmitting}
        className='mb-3'
      />

      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>

        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
        </Button>
      </div>
    </form>
  )
}
